import React, { useState } from "react";

import "react-toastify/dist/ReactToastify.css";

const Contact = (props) => {
  const [form, setForm] = useState({
    subject: "",
    email: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prevForm) => ({
      ...prevForm,
      [name]: value,
    }));
  };

  // form validation
  const validateForm = () => {
    if (!form.email || !form.subject || !form.message) {
      setError(true);
      setErrorMessage("All fields are required");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setError(true);
      setErrorMessage("Please enter a valid email address");
      return false;
    }

    return true;
  };

  // storing data to the strapi
  const storeFormData = async (formData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/form-details`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            data: {
              subject: formData.subject,
              email: formData.email,
              message: formData.message,
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to store form data");
      }

      return await response.json();
    } catch (error) {
      console.error("Error storing form data:", error);
      throw error;
    }
  };

  // send mail
  const sendEmail = async (e) => {
    e.preventDefault();

    setLoading(true);
    setSuccess(false);
    setError(false);
    setErrorMessage("");

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    const dataSend = {
      email: form.email,
      subject: form.subject,
      message: form.message,
    };

    try {
      // Send email
      const res = await fetch(
        `${process.env.REACT_APP_EMAIL_URL}/ppddehemail/ppddehsendEmail`,
        {
          method: "POST",
          body: JSON.stringify(dataSend),
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          mode: "cors",
        }
      );

      const data = await res.json();

      if (res.ok) {
        // Store form data in Strapi
        await storeFormData(dataSend);

        setSuccess(true);
        setForm({
          subject: "",
          email: "",
          message: "",
        });
      } else {
        throw new Error(data.message || "Failed to send message");
      }
    } catch (error) {
      console.error("Error:", error);
      setError(true);
      setErrorMessage(
        error.message ||
          "Failed to process your request. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="text-gray-600 body-font relative">
      {/* map */}
      <div className="absolute inset-0 bg-gray-300">
        <iframe
          className="w-full h-full"
          style={{ filter: "grayscale(1) contrast(1.2) opacity(0.4)" }}
          title="map"
          src="https://www.google.com/maps/d/embed?mid=1DBhwGb5W_2Vu5gPLMN_IsT7_thE&hl=en&ehbc=2E312F"
          width="640"
          height="480"
        />
      </div>

      {/* form */}
      <div className="container px-5 py-24 mx-auto flex">
        <div className="card lg:w-1/3 md:w-1/2 bg-white rounded-lg p-8 flex flex-col md:ml-auto w-full mt-10 md:mt-0 relative z-10 shadow-md">
          <h2 className="text-gray-900 text-lg mb-1 font-medium title-font">
            Contact Me/Feedback
          </h2>
          <p className="leading-relaxed mb-5 text-gray-600">
            Always feel free to contact me for any content requests or feedback.
          </p>
          <div className="relative mb-4">
            <label htmlFor="email" className="leading-7 text-sm text-gray-600">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="What's your email address?"
              className="w-full bg-white rounded border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-base outline-none text-gray-700 py-1 px-3 leading-8 transition-colors duration-200 ease-in-out"
            />
          </div>
          <div className="relative mb-4">
            <label
              htmlFor="subject"
              className="leading-7 text-sm text-gray-600"
            >
              Subject
            </label>
            <input
              type="text"
              name="subject"
              value={form.subject}
              onChange={handleChange}
              placeholder="What's your subject?"
              className="w-full bg-white rounded border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-base outline-none text-gray-700 py-1 px-3 leading-8 transition-colors duration-200 ease-in-out"
            />
          </div>

          <div className="relative mb-4">
            <label
              htmlFor="message"
              className="leading-7 text-sm text-gray-600"
            >
              Message
            </label>
            <textarea
              rows={7}
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder="What you want to say?"
              className="w-full bg-white rounded border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 h-32 text-base outline-none text-gray-700 py-1 px-3 resize-none leading-6 transition-colors duration-200 ease-in-out"
            />
          </div>

          <button
            onClick={sendEmail}
            disabled={loading}
            className="text-white bg-sky-500 border-0 py-2 px-6 focus:outline-none hover:bg-indigo-600 rounded text-lg"
          >
            {loading ? "Sending..." : "Send"}
          </button>

          {/* Success Message */}
          {success && (
            <div className="bg-green-500 text-white p-2 mt-4 rounded-lg">
              Message sent successfully!
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-500 text-white p-2 mt-4 rounded-lg">
              {errorMessage ||
                "Failed to send the message. Please try again later."}
            </div>
          )}

          <p className="text-xs text-gray-500 mt-3">
            I will reply to you as soon as possible. Check out all my contents.
            Thank you :)
          </p>
        </div>
      </div>
    </section>
  );
};

export default Contact;
